<template>
  <div class="AtlasSummaryGeoPicker">
    <div class="AtlasSummaryGeoPicker-inner">
      <div
        class="AtlasSummaryGeoPicker-ctrl AtlasSummaryGeoPicker-ctrl--geoType"
      >
        <div class="AtlasSummaryGeoPicker-label">
          Choose your geography type
        </div>
        <atlas-select :options="geoTypeOptions" @choose="onChooseGeoType" />
      </div>

      <div class="AtlasSummaryGeoPicker-ctrl AtlasSummaryGeoPicker-ctrl--geo">
        <div class="AtlasSummaryGeoPicker-label">Choose your geography</div>
        <atlas-select :options="geoOptions" @choose="onChooseGeo" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import AtlasSelect from '../widget/select.vue';

export default {
  emits: ['pick'],

  components: {
    AtlasSelect,
  },

  props: {
    defaultGeoCode: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    activeGeoType: null,
    activeGeo: null,
    geoTypeOptions: [],
    geoOptions: [],
    geosByType: {},
    geo: null,
  }),

  computed: {
    ...mapState('indicator', ['geoSections']),

    // Geo picker uses its own store, it doesn't have access to breakdown stuff
    // ...mapState('breakdown', [
    //   'primaryGeo',
    // ]),

    geoSectionId() {
      return ((this.geoSections || {})[0] || {}).geo_section_id;
    },

    geoCode() {
      return this.$route.query.geo || this.defaultGeoCode;
    },
  },

  watch: {
    geoSections: {
      immediate: true,
      handler(geoSections) {
        this.geoTypeOptions = geoSections.map((type) => ({
          id: type.id,
          text: type.title,
          isActive: this.activeGeoType === type.id,
        }));
      },
    },

    geoCode: {
      immediate: true,
      async handler(val, oldVal) {
        if (val === oldVal) return;

        const response = await this.$api.get('/geography', {
          params: { geo_code: val },
        });

        [this.geo] = response.data.content;
      },
    },

    geo: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.setGeoType();
        }
      },
    },

    geoSectionId: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.setGeoType();
        }
      },
    },
  },

  methods: {
    async setGeoType() {
      const geoCode = (this.geo || {}).geo_code;

      if (!geoCode || !this.geoSectionId) return;

      const response = await this.$api.get('/geoSectionTypes', {
        params: {
          geo_section_id: this.geoSectionId,
          geo_code: geoCode,
        },
      });

      if (!response.data.content.length) return;

      const option = this.geoTypeOptions.find(
        (o) => o.id === response.data.content[0].id,
      );

      this.onChooseGeoType({ option });
    },

    async onChooseGeoType({ option }) {
      this.activeGeoType = option;
      this.geoTypeOptions = this.geoTypeOptions.map((o) => ({
        ...o,
        isActive: o === option,
      }));

      if (this.geosByType[option.id]) {
        this.geoOptions = this.geosByType[option.id];
      } else {
        this.geoOptions = []; // Reset the select while loading
        const { data } = await this.$api.get('/geoSectionGeos', {
          params: {
            geo_section_type_id: option.id,
            limit: 1000,
            expand: 'geo',
          },
        });

        this.geoOptions = (data.content || []).map((geo) => ({
          text: geo.title,
          id: geo.geography.geo_code,
          type: geo.geography.geo_type,
          to: {
            query: {
              ...this.$route.query,
              geo: geo.geography.geo_code,
              geoSectionName: option.text,
            },
          },
          isActive: false,
        }));

        this.geosByType[option.id] = this.geoOptions;

        if (!this.$route.query.geoSectionName) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              geoSectionName: option.text,
            },
          });
        }
      }

      const activeGeoOption = this.geoOptions.find(
        (o) => o.id === this.geo.geo_code,
      );
      if (activeGeoOption) this.onChooseGeo({ option: activeGeoOption });
    },

    async onChooseGeo({ option }) {
      this.geoOptions = this.geoOptions.map((o) => ({
        ...o,
        isActive: o === option,
      }));

      this.$emit('pick', {
        geoType: this.activeGeoType,
        geo: option,
      });
    },
  },
};
</script>

<style lang="scss">
.AtlasSummaryGeoPicker {
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: inherit;
    box-sizing: inherit;
  }

  &-inner {
    @media screen and (min-width: 667px) {
      display: flex;
    }

    @media screen and (min-width: 1024px) {
      width: 50%;
    }
  }

  &-ctrl {
    flex: 1;

    &--geoType {
      margin-bottom: 20px;

      @media screen and (min-width: 667px) {
        margin-right: 30px;
        margin-bottom: 0;
      }
    }
  }

  &-label {
    margin-bottom: 11px;
    color: color(shadow);
    font-size: 14px;
    font-weight: normal;
    white-space: nowrap;
  }
}
</style>
